// Admin sweep-review page. Operator-facing surface for cross-creator
// bulk triage of operator-pasted sweep-import rows (any `source`
// matching 'search_sweep_<timestamp>'). Hidden from creators by the
// Option A filters in routes/leaks.js (GET /api/leaks + /api/dashboard
// hide these rows); this is the ONLY route surface that lists them.
//
// URL: /admin/sweep-review?source=search_sweep_2026_10_02
//
// Three bulk endpoints behind this page (routes/admin.js, admin-only):
//   GET  /api/admin/leaks/by-source?source=…&creator_id=…&host=…&route=…
//   POST /api/admin/leaks/bulk-approve
//   POST /api/admin/leaks/bulk-mark-safe
//
// Hard contract (enforced by both backend AND this page):
//   - Admin middleware on all three endpoints.
//   - Bulk endpoints HARD-REFUSE any row that is dmca_filed=true
//     (backend returns 409). This page should never offer them for
//     bulk action — the GET endpoint already filters them out.
//   - bulk-approve requires typing the exact count to confirm.
//   - bulk-mark-safe needs a confirm checkbox + safe_reason input
//     (default 'operator_bulk_mark_safe_sweep_review').

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://tribaltech-backend.onrender.com';
const BULK_CAP = 500;
const DEFAULT_SAFE_REASON = 'operator_bulk_mark_safe_sweep_review';

function adminKey() { return sessionStorage.getItem('cp_admin_key') || ''; }
function adminHeaders(extra) { return { 'X-Admin-Key': adminKey(), ...(extra || {}) }; }

// Route-label color/label map. Backend emits one of these per row via
// resolveDmcaRouting; keep this map in sync if new labels appear.
const ROUTE_META = {
  email:            { label: 'email route',    color: '#4ade80', bg: '#16653422' },
  manual_form:      { label: 'manual form',    color: '#fb923c', bg: '#9a340022' },
  host_abuse_only:  { label: 'host-abuse only', color: '#f87171', bg: '#7f1d1d22' },
  pilot_email:      { label: 'pilot email',    color: '#a78bfa', bg: '#4c1d9522' },
  no_route:         { label: 'no route',        color: '#64748b', bg: '#1e293b22' },
};
function routeChip(label) {
  const m = ROUTE_META[label] || ROUTE_META.no_route;
  return (
    <span style={{ ...S.chip, color: m.color, background: m.bg, borderColor: m.color + '66' }}>
      {m.label}
    </span>
  );
}

export default function AdminSweepReview() {
  // Auth gate — mirrors AdminLeaks. sessionStorage is per-tab, so an
  // operator opening /admin/sweep-review in a new tab (bookmark, fresh
  // window) has no cp_admin_key and would otherwise hit a raw 403 from
  // adminMiddleware. Redirect to /admin for the password prompt first.
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const source = searchParams.get('source') || '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [filterCreator, setFilterCreator] = useState('all');
  const [filterHost, setFilterHost]       = useState('all');
  const [filterRoute, setFilterRoute]     = useState('all');
  // 'all' | 'only' | 'exclude' — filter rows by whether the host is on
  // NEEDS_DIFFERENT_ROUTE_HOSTS (operator-handled, not for automated
  // bundles). 'only' is the "pull all 38+ in one view" workflow.
  const [filterDiffRoute, setFilterDiffRoute] = useState('all');
  const [toast, setToast] = useState(null);

  // Modals
  const [approveModal, setApproveModal] = useState(null); // null | { count, typed }
  const [markSafeModal, setMarkSafeModal] = useState(null); // null | { count, confirmed, reason }

  useEffect(() => {
    if (!adminKey()) {
      navigate('/admin', { replace: true });
      return;
    }
    setAuthChecked(true);
  }, [navigate]);

  useEffect(() => {
    if (!authChecked || !source) return;
    setLoading(true);
    setErr(null);
    fetch(`${API}/api/admin/leaks/by-source?source=${encodeURIComponent(source)}`, { headers: adminHeaders() })
      .then(r => r.ok ? r.json() : r.text().then(t => { throw new Error(`HTTP ${r.status}: ${t.slice(0, 200)}`); }))
      .then(d => { setRows(d.rows || []); setSelected(new Set()); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [authChecked, source]);

  const creatorOptions = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const key = r.creator_id;
      const label = r.stage_name || `(no-stage) [${(r.creator_id || '').slice(0, 8)}]`;
      if (!m.has(key)) m.set(key, label);
    }
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const hostOptions = useMemo(() => {
    return [...new Set(rows.map(r => r.source_host).filter(Boolean))].sort();
  }, [rows]);

  const routeOptions = useMemo(() => {
    return [...new Set(rows.map(r => r.route_label).filter(Boolean))].sort();
  }, [rows]);

  const filtered = useMemo(() => rows.filter(r =>
    (filterCreator   === 'all' || r.creator_id === filterCreator) &&
    (filterHost      === 'all' || r.source_host === filterHost) &&
    (filterRoute     === 'all' || r.route_label === filterRoute) &&
    (filterDiffRoute === 'all'
      || (filterDiffRoute === 'only'    &&  !!r.needs_different_route)
      || (filterDiffRoute === 'exclude' &&  !r.needs_different_route))
  ), [rows, filterCreator, filterHost, filterRoute, filterDiffRoute]);

  // Backend returns rows already sorted by creator → host → detected_at DESC.
  // The filtered view inherits that order.

  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r.id));
  function toggleAllFiltered() {
    if (allFilteredSelected) {
      const next = new Set(selected);
      for (const r of filtered) next.delete(r.id);
      setSelected(next);
    } else {
      const next = new Set(selected);
      for (const r of filtered) next.add(r.id);
      setSelected(next);
    }
  }
  function toggleRow(id) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  }

  async function doBulkApprove() {
    const ids = [...selected];
    try {
      const r = await fetch(`${API}/api/admin/leaks/bulk-approve`, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ leak_ids: ids, expected_count: ids.length }),
      });
      const d = await r.json();
      if (!r.ok) {
        setToast({ kind: 'err', text: `Approve failed: ${d.error || d.message || r.status}` });
        return;
      }
      setToast({ kind: 'ok', text: `Approved: flipped ${d.flipped}, skipped ${d.skipped_wrong_state}` });
      // Remove flipped rows from view
      const flippedSet = new Set(d.flipped_ids);
      setRows(rows.filter(r => !flippedSet.has(r.id)));
      setSelected(new Set());
    } catch (e) {
      setToast({ kind: 'err', text: `Approve error: ${e.message}` });
    }
    setApproveModal(null);
  }

  async function doBulkMarkSafe(reason) {
    const ids = [...selected];
    try {
      const r = await fetch(`${API}/api/admin/leaks/bulk-mark-safe`, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ leak_ids: ids, expected_count: ids.length, safe_reason: reason }),
      });
      const d = await r.json();
      if (!r.ok) {
        setToast({ kind: 'err', text: `Mark-safe failed: ${d.error || d.message || r.status}` });
        return;
      }
      setToast({ kind: 'ok', text: `Mark-safe: flipped ${d.flipped}, skipped ${d.skipped_wrong_state} (reason: ${d.safe_reason})` });
      const flippedSet = new Set(d.flipped_ids);
      setRows(rows.filter(r => !flippedSet.has(r.id)));
      setSelected(new Set());
    } catch (e) {
      setToast({ kind: 'err', text: `Mark-safe error: ${e.message}` });
    }
    setMarkSafeModal(null);
  }

  if (!source) {
    return (
      <div style={S.page}>
        <h1 style={S.h1}>Sweep Review</h1>
        <p style={S.err}>Missing <code>?source=search_sweep_&lt;timestamp&gt;</code> query parameter.</p>
      </div>
    );
  }

  const selectedCount = selected.size;
  const overCap = selectedCount > BULK_CAP;

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Sweep Review</h1>
      <p style={S.sub}>
        <code>source={source}</code> · {rows.length} rows · {filtered.length} filtered · {selectedCount} selected
        {overCap && <span style={{ color: '#f87171', marginLeft: 12 }}>⚠ selected &gt; cap ({BULK_CAP})</span>}
      </p>

      {err && <div style={S.err}>Error loading rows: {err}</div>}
      {toast && (
        <div style={{ ...S.toast, borderColor: toast.kind === 'ok' ? '#16a34a' : '#dc2626' }}>
          {toast.text}
          <button style={S.closeToast} onClick={() => setToast(null)}>×</button>
        </div>
      )}

      {/* Filters */}
      <div style={S.filters}>
        <select style={S.select} value={filterCreator} onChange={e => setFilterCreator(e.target.value)}>
          <option value="all">All creators</option>
          {creatorOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
        <select style={S.select} value={filterHost} onChange={e => setFilterHost(e.target.value)}>
          <option value="all">All hosts</option>
          {hostOptions.map(h => <option key={h} value={h}>{h}</option>)}
        </select>
        <select style={S.select} value={filterRoute} onChange={e => setFilterRoute(e.target.value)}>
          <option value="all">All routes</option>
          {routeOptions.map(r => <option key={r} value={r}>{ROUTE_META[r]?.label || r}</option>)}
        </select>
        <select
          style={S.select}
          value={filterDiffRoute}
          onChange={e => setFilterDiffRoute(e.target.value)}
          title="Rows whose host is on NEEDS_DIFFERENT_ROUTE_HOSTS — DMCA isn't the right lever; operator-handled."
        >
          <option value="all">Different-route: all</option>
          <option value="only">Different-route: only</option>
          <option value="exclude">Different-route: exclude</option>
        </select>
      </div>

      {/* Bulk action bar */}
      <div style={S.actionBar}>
        <button
          style={{ ...S.btn, ...(selectedCount === 0 || overCap ? S.btnDisabled : S.btnApprove) }}
          disabled={selectedCount === 0 || overCap}
          onClick={() => setApproveModal({ count: selectedCount, typed: '' })}>
          Approve selected ({selectedCount})
        </button>
        <button
          style={{ ...S.btn, ...(selectedCount === 0 || overCap ? S.btnDisabled : S.btnSafe) }}
          disabled={selectedCount === 0 || overCap}
          onClick={() => setMarkSafeModal({ count: selectedCount, confirmed: false, reason: DEFAULT_SAFE_REASON })}>
          Mark Safe selected ({selectedCount})
        </button>
      </div>

      {loading && <p>Loading…</p>}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}><input type="checkbox" checked={allFilteredSelected} onChange={toggleAllFiltered} /></th>
              <th style={S.th}>Creator</th>
              <th style={S.th}>Host</th>
              <th style={S.th}>Route</th>
              <th style={S.th}>Flags</th>
              <th style={S.th}>URL</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} style={selected.has(r.id) ? S.rowSel : S.row}>
                <td style={S.td}>
                  <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleRow(r.id)} />
                </td>
                <td style={S.td}>{r.stage_name || <span style={S.muted}>(no-stage) [{(r.creator_id || '').slice(0, 8)}]</span>}</td>
                <td style={S.td}>{r.source_host}</td>
                <td style={S.td}>{routeChip(r.route_label)}</td>
                <td style={S.td}>
                  {r.sensitive_alias_hit && (
                    <span style={S.sensitiveBadge} title="Slug contains a sensitive_aliases token for this creator — do NOT bulk-approve into a public-channel batch">
                      ⚠ sensitive alias
                    </span>
                  )}
                  {r.needs_different_route && (
                    <span
                      style={S.diffRouteBadge}
                      title={`DMCA is not the right lever for this host — handle via ${r.needs_different_route.route}. ${r.needs_different_route.reason}`}
                    >
                      ⚠ needs {r.needs_different_route.route.replace(/_/g, ' ')}
                    </span>
                  )}
                </td>
                <td style={S.td}>
                  <a href={r.leaked_url} target="_blank" rel="noopener noreferrer" style={S.urlLink}>{r.leaked_url}</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!loading && filtered.length === 0 && <p style={S.muted}>No rows match the current filters.</p>}

      {/* Approve modal */}
      {approveModal && (
        <div style={S.modalOverlay} onClick={() => setApproveModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h2 style={S.modalH}>Approve selected rows</h2>
            <p>You are about to flip <strong>{approveModal.count}</strong> rows to <code>needs_review=false</code> (actionable).</p>
            <p>Backend hard-refuses any row with <code>dmca_filed=true</code> (409). Rows in wrong state are skipped.</p>
            <p>Type the exact count to confirm:</p>
            <input style={S.input} value={approveModal.typed} onChange={e => setApproveModal({ ...approveModal, typed: e.target.value })} placeholder={`type ${approveModal.count}`} autoFocus />
            <div style={S.modalBtns}>
              <button style={{ ...S.btn, ...S.btnCancel }} onClick={() => setApproveModal(null)}>Cancel</button>
              <button
                style={{ ...S.btn, ...(String(approveModal.count) === approveModal.typed.trim() ? S.btnApprove : S.btnDisabled) }}
                disabled={String(approveModal.count) !== approveModal.typed.trim()}
                onClick={doBulkApprove}>
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Safe modal */}
      {markSafeModal && (
        <div style={S.modalOverlay} onClick={() => setMarkSafeModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h2 style={S.modalH}>Mark Safe selected rows</h2>
            <p>You are about to flip <strong>{markSafeModal.count}</strong> rows to <code>is_safe=true</code> with a stamped reason.</p>
            <label style={S.labelLine}>safe_reason:</label>
            <input style={S.input} value={markSafeModal.reason} onChange={e => setMarkSafeModal({ ...markSafeModal, reason: e.target.value })} />
            <label style={S.labelLine}>
              <input type="checkbox" checked={markSafeModal.confirmed} onChange={e => setMarkSafeModal({ ...markSafeModal, confirmed: e.target.checked })} />
              {' '}I confirm these rows should be marked safe (not actionable).
            </label>
            <div style={S.modalBtns}>
              <button style={{ ...S.btn, ...S.btnCancel }} onClick={() => setMarkSafeModal(null)}>Cancel</button>
              <button
                style={{ ...S.btn, ...(markSafeModal.confirmed && markSafeModal.reason.trim().length >= 4 ? S.btnSafe : S.btnDisabled) }}
                disabled={!markSafeModal.confirmed || markSafeModal.reason.trim().length < 4}
                onClick={() => doBulkMarkSafe(markSafeModal.reason.trim())}>
                Mark Safe
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  page:   { minHeight: '100vh', background: '#0b0b14', color: '#e2e8f0', padding: '32px 24px', fontFamily: 'system-ui, sans-serif' },
  h1:     { fontSize: 24, fontWeight: 700, margin: 0, marginBottom: 8 },
  sub:    { color: '#94a3b8', fontSize: 13, marginBottom: 20 },
  err:    { background: '#7f1d1d33', border: '1px solid #dc262666', color: '#fca5a5', padding: 12, borderRadius: 8, marginBottom: 16 },
  toast:  { background: '#1e293b', border: '1px solid', padding: 12, borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeToast: { background: 'none', border: 'none', color: '#e2e8f0', fontSize: 18, cursor: 'pointer' },
  filters: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  select: { background: '#1e293b', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '6px 10px', fontSize: 13 },
  actionBar: { display: 'flex', gap: 10, marginBottom: 16 },
  btn:    { padding: '8px 16px', borderRadius: 6, border: '1px solid', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 },
  btnApprove: { background: '#16a34a22', color: '#4ade80', borderColor: '#16a34a66' },
  btnSafe:    { background: '#64748b22', color: '#cbd5e1', borderColor: '#64748b66' },
  btnCancel:  { background: 'transparent', color: '#94a3b8', borderColor: '#64748b66' },
  btnDisabled: { background: '#1e293b', color: '#475569', borderColor: '#334155', cursor: 'not-allowed' },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:     { textAlign: 'left', padding: '10px 8px', background: '#1e293b', color: '#cbd5e1', borderBottom: '1px solid rgba(255,255,255,0.1)', fontWeight: 600 },
  td:     { padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)', verticalAlign: 'top' },
  row:    {},
  rowSel: { background: '#4c1d9522' },
  urlLink: { color: '#60a5fa', textDecoration: 'none', wordBreak: 'break-all' },
  muted:  { color: '#64748b', fontStyle: 'italic' },
  chip:   { fontSize: 11, padding: '3px 8px', borderRadius: 10, border: '1px solid', fontWeight: 600, whiteSpace: 'nowrap' },
  sensitiveBadge: { fontSize: 11, padding: '3px 8px', borderRadius: 10, background: '#dc262622', color: '#fca5a5', border: '1px solid #dc262666', fontWeight: 600 },
  diffRouteBadge: { fontSize: 11, padding: '3px 8px', borderRadius: 10, background: '#f59e0b22', color: '#fbbf24', border: '1px solid #f59e0b66', fontWeight: 600, marginLeft: 6 },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:  { background: '#0b0b14', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, padding: 24, width: 480, maxWidth: '90vw' },
  modalH: { fontSize: 18, fontWeight: 700, margin: 0, marginBottom: 12 },
  input:  { width: '100%', padding: '8px 12px', borderRadius: 6, background: '#1e293b', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.1)', fontSize: 13, marginBottom: 12, boxSizing: 'border-box' },
  labelLine: { display: 'block', fontSize: 13, color: '#cbd5e1', marginBottom: 6 },
  modalBtns: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 },
};
